/**
 * MeCustomerCare — /me/customer-care  (Iter 117)
 *
 * The customer's view of their support tickets: submitted grievances,
 * status, mood chip, priority, staff replies, and a voice-playback link
 * when they recorded a voice note. Closed tickets auto-hide 10 days
 * after resolution (backend policy) — a banner tells the customer so.
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { custApi, formatErr } from "../../lib/customerApi";
import { toast } from "sonner";
import {
  ChatCircleDots, Ticket, Warning, CheckCircle, Clock, X,
  Info, PaperPlaneTilt, CaretRight, Microphone, ArrowLeft,
} from "@phosphor-icons/react";
import { GrievanceLauncher } from "./MeDashboard";

const STATUS_META = {
  open:        { bg: "bg-[#F4C7B9]", fg: "text-[#7A2E18]", label: "open" },
  in_progress: { bg: "bg-[#F9E4B7]", fg: "text-[#7A5A11]", label: "in progress" },
  resolved:    { bg: "bg-[#FEE2E2]", fg: "text-[#7F1D1D]", label: "resolved" },
  closed:      { bg: "bg-[#E5E2D9]", fg: "text-[#5C5A55]", label: "closed" },
};

const MOOD_META = {
  angry:    { bg: "bg-[#F4C7B9]", fg: "text-[#7A2E18]", icon: "😠", label: "angry" },
  very_bad: { bg: "bg-[#F4C7B9]", fg: "text-[#7A2E18]", icon: "😢", label: "very bad" },
  bad:      { bg: "bg-[#F9E4B7]", fg: "text-[#7A5A11]", icon: "😕", label: "bad" },
  normal:   null,
  good:     { bg: "bg-[#FEE2E2]", fg: "text-[#7F1D1D]", icon: "🙂", label: "good" },
};

export default function MeCustomerCare() {
  const [loading, setLoading] = useState(true);
  const [tickets, setTickets] = useState([]);
  const [hideAfterDays, setHideAfterDays] = useState(10);
  const [selected, setSelected] = useState(null);      // ticket_id | null

  const load = async () => {
    try {
      const r = await custApi.get("/me/tickets");
      setTickets(r.data.tickets || []);
      setHideAfterDays(r.data.hide_after_days || 10);
    } catch (e) {
      toast.error(formatErr(e));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openTickets = useMemo(
    () => tickets.filter(t => !["closed","resolved"].includes(t.status)),
    [tickets]
  );
  const doneTickets = useMemo(
    () => tickets.filter(t => ["closed","resolved"].includes(t.status)),
    [tickets]
  );

  if (selected) {
    return (
      <MeTicketDetail
        ticketId={selected}
        onBack={() => setSelected(null)}
      />
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-5 space-y-4"
         data-testid="me-customer-care">
      <div className="flex items-center justify-between">
        <div>
          <div className="font-heading text-xl">Customer care</div>
          <div className="text-[11px] text-[#5C5A55] mt-0.5">
            Your grievances & support tickets
          </div>
        </div>
        <Link to="/me"
              className="text-[11px] uppercase tracking-[0.14em] text-[#5C5A55] hover:text-[#1A1A1A] inline-flex items-center gap-1">
          <ArrowLeft size={11}/>Back
        </Link>
      </div>

      {/* Iter 117 — auto-delete notice banner */}
      <div className="rounded-md border border-[#E5E2D9] bg-[#FFFDF5] p-3 flex gap-2 items-start"
           data-testid="me-cc-notice">
        <Info size={16} className="text-[#7A5A11] shrink-0 mt-0.5"/>
        <div className="text-[12px] text-[#5C5A55] leading-relaxed">
          <strong className="text-[#1A1A1A]">Dear customer,</strong> your closed tickets are
          automatically removed from this app after <strong>{hideAfterDays} days</strong> for
          smooth app performance. They remain safely with our support
          team should you need to reference them later.
        </div>
      </div>

      {/* Iter 118 — mount the actual grievance launcher inline so the
          drawer opens directly (was linking to /me and never opening). */}
      <div data-testid="me-cc-new-grievance">
        <GrievanceLauncher triggerText="Submit a grievance" onSubmitted={load}/>
      </div>

      {loading && (
        <div className="text-center py-10 text-[#8A8780] text-sm">Loading…</div>
      )}

      {!loading && tickets.length === 0 && (
        <div className="text-center py-10 border border-dashed border-[#E5E2D9] rounded-md">
          <ChatCircleDots size={28} className="inline-block text-[#8A8780] mb-2 opacity-60"/>
          <div className="text-sm text-[#5C5A55]">No tickets yet.</div>
          <div className="text-[11px] text-[#8A8780] mt-1">
            When you submit a grievance, it will show up here.
          </div>
        </div>
      )}

      {!loading && openTickets.length > 0 && (
        <Section title="Active" count={openTickets.length}>
          {openTickets.map(t => (
            <TicketCard key={t.id} t={t} onOpen={() => setSelected(t.id)}/>
          ))}
        </Section>
      )}

      {!loading && doneTickets.length > 0 && (
        <Section title="Resolved / closed" count={doneTickets.length}
                  hint="auto-removes after 10 days from close">
          {doneTickets.map(t => (
            <TicketCard key={t.id} t={t} onOpen={() => setSelected(t.id)}/>
          ))}
        </Section>
      )}
    </div>
  );
}

function Section({ title, count, hint, children }) {
  return (
    <div>
      <div className="flex items-baseline gap-2 mb-1.5">
        <div className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55]">
          {title} <span className="text-[#8A8780]">· {count}</span>
        </div>
        {hint && <div className="text-[9px] text-[#8A8780]">{hint}</div>}
      </div>
      <div className="space-y-1.5">{children}</div>
    </div>
  );
}

function TicketCard({ t, onOpen }) {
  const st = STATUS_META[t.status] || STATUS_META.open;
  const mood = MOOD_META[t.mood];
  const isDone = t.status === "closed" || t.status === "resolved";
  const hot = !isDone && (t.mood === "angry" || t.mood === "very_bad" || t.priority === "high");
  return (
    <button type="button" onClick={onOpen}
            data-testid={`me-cc-ticket-${t.id}`}
            className={`w-full text-left rounded-md border border-[#E5E2D9] bg-white p-3 hover:border-[#B5D5AC] transition-colors ${
              hot ? "tkt-blink-row" : ""
            }`}>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="font-mono text-[13px] font-semibold text-[#1A1A1A]">{t.ticket_no}</span>
        <span className={`px-2 py-0.5 text-[9px] uppercase tracking-[0.14em] rounded font-semibold ${st.bg} ${st.fg}`}>
          {st.label}
        </span>
        {t.priority === "high" && (
          <span className="px-2 py-0.5 text-[9px] uppercase tracking-[0.14em] rounded bg-[#F4C7B9] text-[#7A2E18] font-semibold">
            high
          </span>
        )}
        {mood && (
          <span className={`px-2 py-0.5 text-[9px] uppercase tracking-[0.14em] rounded font-semibold ${mood.bg} ${mood.fg}`}
                title={`Recorded mood: ${mood.label}`}>
            {mood.icon} {mood.label}
          </span>
        )}
        {t.voice_id && (
          <span className="text-[9px] uppercase tracking-[0.14em] text-[#DC2626] inline-flex items-center gap-0.5">
            <Microphone size={9} weight="fill"/>voice
          </span>
        )}
        <CaretRight size={12} className="ml-auto text-[#8A8780]"/>
      </div>
      <div className="text-sm text-[#1A1A1A] mt-1 line-clamp-2">{t.issue}</div>
      <div className="flex items-center justify-between mt-1.5">
        <div className="text-[10px] text-[#8A8780]">
          Filed {new Date(t.created_at).toLocaleDateString()}
        </div>
        {t.replies_count > 0 ? (
          <div className="text-[10px] text-[#DC2626] inline-flex items-center gap-0.5 font-medium">
            <ChatCircleDots size={11} weight="fill"/>
            {t.replies_count} reply{t.replies_count > 1 ? "s" : ""} from us
          </div>
        ) : !isDone ? (
          <div className="text-[10px] text-[#8A8780] inline-flex items-center gap-0.5">
            <Clock size={10}/>Awaiting our response
          </div>
        ) : null}
      </div>
    </button>
  );
}

// -------- Ticket detail — replies thread + full context --------
function MeTicketDetail({ ticketId, onBack }) {
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    let alive = true;
    custApi.get(`/me/tickets/${ticketId}`)
      .then(r => { if (alive) setData(r.data); })
      .catch(e => { if (alive) toast.error(formatErr(e)); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [ticketId]);

  if (loading) return <div className="p-6 text-sm text-[#5C5A55]">Loading…</div>;
  if (!data) return null;
  const { ticket, replies } = data;
  const st = STATUS_META[ticket.status] || STATUS_META.open;
  const isDone = ticket.status === "closed" || ticket.status === "resolved";

  return (
    <div className="max-w-2xl mx-auto px-4 py-5 space-y-4"
         data-testid="me-cc-detail">
      <button onClick={onBack}
              data-testid="me-cc-detail-back"
              className="text-[11px] uppercase tracking-[0.14em] text-[#5C5A55] hover:text-[#1A1A1A] inline-flex items-center gap-1">
        <ArrowLeft size={11}/>Back to tickets
      </button>

      <div className="rounded-md border border-[#E5E2D9] bg-white p-4">
        <div className="flex items-center gap-2 flex-wrap mb-2">
          <span className="font-mono text-sm font-semibold">{ticket.ticket_no}</span>
          <span className={`px-2 py-0.5 text-[9px] uppercase tracking-[0.14em] rounded font-semibold ${st.bg} ${st.fg}`}>
            {st.label}
          </span>
          {ticket.priority === "high" && (
            <span className="px-2 py-0.5 text-[9px] uppercase tracking-[0.14em] rounded bg-[#F4C7B9] text-[#7A2E18] font-semibold">
              high priority
            </span>
          )}
        </div>
        <div className="text-sm text-[#1A1A1A] whitespace-pre-wrap">{ticket.issue}</div>
        <div className="text-[10px] text-[#8A8780] mt-2">
          Filed on {new Date(ticket.created_at).toLocaleString()}
        </div>
        {isDone && (ticket.resolved_at || ticket.closed_at) && (
          <div className="text-[10px] text-[#DC2626] mt-0.5">
            <CheckCircle size={10} weight="fill" className="inline-block mr-0.5"/>
            Marked {ticket.status} on {new Date(ticket.resolved_at || ticket.closed_at).toLocaleDateString()}
          </div>
        )}
        {ticket.resolution && (
          <div className="mt-2 p-2 rounded bg-[#FEE2E2] border border-[#FEE2E2] text-[12px]">
            <div className="text-[9px] uppercase tracking-[0.14em] font-semibold text-[#7F1D1D] mb-0.5">
              Our resolution
            </div>
            <div className="text-[#7F1D1D] whitespace-pre-wrap">{ticket.resolution}</div>
          </div>
        )}
      </div>

      {/* Replies thread */}
      <div>
        <div className="text-[11px] uppercase tracking-[0.2em] text-[#5C5A55] mb-1.5">
          Messages from our team · {replies.length}
        </div>
        {replies.length === 0 ? (
          <div className="rounded-md border border-dashed border-[#E5E2D9] p-4 text-center">
            <div className="text-[12px] text-[#8A8780]">
              No messages yet. Our team will respond here as soon as we look into your ticket.
            </div>
          </div>
        ) : (
          <div className="space-y-1.5" data-testid="me-cc-reply-list">
            {replies.map(r => (
              <div key={r.id} className="flex justify-start"
                   data-testid={`me-cc-reply-${r.id}`}>
                <div className="max-w-[85%] rounded-2xl rounded-bl-none bg-white border border-[#FEE2E2] px-3 py-2 text-[13px]">
                  <div className="text-[9px] uppercase tracking-[0.12em] font-semibold text-[#DC2626] mb-0.5">
                    {r.author_name || "Rescigo team"}
                  </div>
                  <div className="whitespace-pre-wrap leading-snug">{r.body}</div>
                  <div className="text-[9px] mt-0.5 text-[#8A8780]">
                    {new Date(r.created_at).toLocaleString()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
